/**
 * FormBuilderCanvas - Center panel of the form builder.
 *
 * Renders form sections as cards with draggable controls.
 * Supports dropping field types from the library and reordering controls.
 */

import { cn } from '@/shared/lib/cn';
import { Badge, Button, Input } from '@/shared/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  GripVertical,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { formatControlType } from './builder-utils';
import type { FieldTemplate, FormControl, FormSection, SelectedTarget } from './types';

const DND_TYPE_FIELD = 'FIELD_TYPE';
const DND_TYPE_CONTROL = 'CONTROL';

interface ControlCardProps {
  control: FormControl;
  sectionIndex: number;
  controlIndex: number;
  isSelected: boolean;
  onSelect: () => void;
  onMoveControl: (from: number, to: number) => void;
  onRemove: () => void;
}

function ControlCard({
  control,
  sectionIndex,
  controlIndex,
  isSelected,
  onSelect,
  onMoveControl,
  onRemove,
}: ControlCardProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: DND_TYPE_CONTROL,
    item: { sectionIndex, controlIndex },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }));

  const [{ isOver }, drop] = useDrop<
    { sectionIndex: number; controlIndex: number },
    void,
    { isOver: boolean }
  >(() => ({
    accept: DND_TYPE_CONTROL,
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
    hover: (item, monitor) => {
      if (item.sectionIndex !== sectionIndex) return;
      if (item.controlIndex === controlIndex) return;
      const hoverRect = ref.current?.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!hoverRect || !clientOffset) return;
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const hoverClientY = clientOffset.y - hoverRect.top;
      if (item.controlIndex < controlIndex && hoverClientY < hoverMiddleY) return;
      if (item.controlIndex > controlIndex && hoverClientY > hoverMiddleY) return;
      onMoveControl(item.controlIndex, controlIndex);
      item.controlIndex = controlIndex;
    },
  }));

  drag(drop(ref));

  return (
    <div
      ref={ref}
      data-control-card="true"
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-3 py-2 mb-1 cursor-pointer transition-all',
        isSelected
          ? 'border-[#E84A2B] bg-orange-50/50 dark:bg-primary/10'
          : 'border-zinc-200 bg-white hover:border-zinc-300 dark:border-border dark:bg-card dark:hover:border-muted-foreground/50',
        isDragging && 'opacity-40',
        isOver && 'border-blue-400 dark:border-blue-400',
      )}
    >
      <GripVertical className="w-3.5 h-3.5 text-zinc-400 dark:text-muted-foreground flex-shrink-0 cursor-grab" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-800 dark:text-foreground truncate">
          {control.control_name || 'Untitled field'}
        </p>
        <p className="text-xs text-zinc-500 dark:text-muted-foreground">
          {formatControlType(control.control_type)} · Q{control.q_id}
        </p>
      </div>
      {control.required && (
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex-shrink-0">
          Required
        </Badge>
      )}
      {control.hidden && (
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex-shrink-0">
          Hidden
        </Badge>
      )}
      {control.read_only && (
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex-shrink-0">
          Read only
        </Badge>
      )}
      <button
        type="button"
        className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-accent transition-colors flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        title="Delete field"
      >
        <Trash2 className="w-3.5 h-3.5 text-zinc-400 hover:text-red-500 dark:text-muted-foreground dark:hover:text-red-500" />
      </button>
    </div>
  );
}

interface SectionCardProps {
  section: FormSection;
  sectionIndex: number;
  totalSections: number;
  selected: SelectedTarget | null;
  onSelectSection: () => void;
  onSelectControl: (controlIndex: number) => void;
  onAddControl: (template: FieldTemplate, position?: number) => void;
  onDeleteSection: () => void;
  onDeleteControl: (controlIndex: number) => void;
  onMoveControl: (from: number, to: number) => void;
  onMoveControlToSection: (fromSection: number, fromIndex: number, toSection: number) => void;
  onDuplicateSection: () => void;
  onRenameSection: (name: string) => void;
}

function SectionCard({
  section,
  sectionIndex,
  totalSections,
  selected,
  onSelectSection,
  onSelectControl,
  onAddControl,
  onDeleteSection,
  onDeleteControl,
  onMoveControl,
  onMoveControlToSection,
  onDuplicateSection,
  onRenameSection,
}: SectionCardProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(section.section_name || '');

  useEffect(() => {
    if (!isEditing) setEditName(section.section_name || '');
  }, [section.section_name, isEditing]);

  const [{ isOver }, sectionDrop] = useDrop(() => ({
    accept: [DND_TYPE_FIELD, DND_TYPE_CONTROL],
    drop: (item: { template?: FieldTemplate; sectionIndex?: number; controlIndex?: number }) => {
      if (item.template) {
        onAddControl(item.template);
        return;
      }
      if (
        typeof item.sectionIndex === 'number' &&
        item.sectionIndex !== sectionIndex &&
        typeof item.controlIndex === 'number'
      ) {
        onMoveControlToSection(item.sectionIndex, item.controlIndex, sectionIndex);
        item.sectionIndex = sectionIndex;
        item.controlIndex = section.controls.length;
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
  }));

  sectionDrop(sectionRef);

  const handleRenameSave = () => {
    const trimmed = editName.trim();
    if (trimmed) onRenameSection(trimmed);
    setIsEditing(false);
  };

  return (
    <div
      ref={sectionRef}
      className={cn(
        'rounded-lg border bg-white dark:bg-card mb-3 transition-colors',
        isOver
          ? 'border-[#E84A2B] bg-orange-50/30 dark:bg-primary/10'
          : 'border-zinc-200 dark:border-border',
      )}
    >
      {/* Section header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={onSelectSection}
          onDoubleClick={() => setIsEditing(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSelectSection();
          }}
        >
          {isEditing ? (
            <Input
              value={editName}
              autoFocus
              className="h-7 text-sm font-semibold"
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleRenameSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSave();
                if (e.key === 'Escape') setIsEditing(false);
              }}
            />
          ) : (
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-zinc-800 dark:text-foreground truncate">
                {section.section_name || `Section ${sectionIndex + 1}`}
              </h4>
              {section.is_repeatable && (
                <Badge variant="secondary" className="text-[10px]">
                  Repeatable
                </Badge>
              )}
            </div>
          )}
          <p className="text-xs text-zinc-500 dark:text-muted-foreground mt-0.5">
            {section.controls.length} {section.controls.length === 1 ? 'field' : 'fields'}
          </p>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-accent transition-colors"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? (
              <ChevronDown className="w-4 h-4 text-zinc-400 dark:text-muted-foreground" />
            ) : (
              <ChevronUp className="w-4 h-4 text-zinc-400 dark:text-muted-foreground" />
            )}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-accent transition-colors"
                title="Section actions"
              >
                <MoreVertical className="w-4 h-4 text-zinc-400 dark:text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                <Pencil className="w-3.5 h-3.5 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicateSection}>
                <Copy className="w-3.5 h-3.5 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDeleteSection}
                className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Delete section
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Section body */}
      {!collapsed && (
        <div className="px-4 pb-4">
          {section.controls.length === 0 ? (
            <div
              className={cn(
                'rounded-lg border border-dashed py-6 text-center transition-colors',
                isOver
                  ? 'border-[#E84A2B] bg-orange-50/40 dark:bg-primary/10'
                  : 'border-zinc-300 dark:border-border',
              )}
            >
              <p className="text-xs text-zinc-500 dark:text-muted-foreground">
                {isOver ? 'Drop here to add the field' : 'Drag fields here to get started'}
              </p>
            </div>
          ) : (
            section.controls.map((control, controlIndex) => (
              <ControlCard
                key={`${control.q_id}-${controlIndex}`}
                control={control}
                sectionIndex={sectionIndex}
                controlIndex={controlIndex}
                isSelected={
                  selected?.sectionIndex === sectionIndex && selected?.controlIndex === controlIndex
                }
                onSelect={() => onSelectControl(controlIndex)}
                onMoveControl={(from, to) => onMoveControl(from, to)}
                onRemove={() => onDeleteControl(controlIndex)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface FormBuilderCanvasProps {
  sections: FormSection[];
  selected: SelectedTarget | null;
  onSelectSection: (sectionIndex: number) => void;
  onSelectControl: (sectionIndex: number, controlIndex: number) => void;
  onAddSection: () => void;
  onDeleteSection: (sectionIndex: number) => void;
  onDeleteControl: (sectionIndex: number, controlIndex: number) => void;
  onAddControl: (sectionIndex: number, template: FieldTemplate, position?: number) => void;
  onMoveControl: (sectionIndex: number, fromIndex: number, toIndex: number) => void;
  onMoveControlToSection: (fromSection: number, fromIndex: number, toSection: number) => void;
  onDuplicateSection: (sectionIndex: number) => void;
  onRenameSection: (sectionIndex: number, name: string) => void;
}

export default function FormBuilderCanvas({
  sections,
  selected,
  onSelectSection,
  onSelectControl,
  onAddSection,
  onDeleteSection,
  onDeleteControl,
  onAddControl,
  onMoveControl,
  onMoveControlToSection,
  onDuplicateSection,
  onRenameSection,
}: FormBuilderCanvasProps) {
  return (
    <div className="border rounded-lg bg-zinc-50/50 dark:bg-muted/30 h-full flex flex-col">
      {/* Canvas header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white dark:bg-card rounded-t-lg">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Canvas</h3>
          <p className="text-xs text-zinc-500 dark:text-muted-foreground">
            Arrange sections and fields
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onAddSection} className="text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add Section
        </Button>
      </div>

      {/* Canvas body */}
      <div className="flex-1 overflow-y-auto p-4">
        {sections.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-300 dark:border-border py-12 text-center">
            <p className="text-sm font-medium text-zinc-500 dark:text-muted-foreground">
              No sections yet
            </p>
            <p className="text-xs text-zinc-400 dark:text-muted-foreground mt-1">
              Add a section to start building your form
            </p>
            <Button variant="outline" size="sm" onClick={onAddSection} className="mt-4 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add Section
            </Button>
          </div>
        ) : (
          sections.map((section, sectionIndex) => (
            <SectionCard
              key={section.id ?? section.section_number ?? sectionIndex}
              section={section}
              sectionIndex={sectionIndex}
              totalSections={sections.length}
              selected={selected}
              onSelectSection={() => onSelectSection(sectionIndex)}
              onSelectControl={(controlIndex) => onSelectControl(sectionIndex, controlIndex)}
              onAddControl={(template, position) => onAddControl(sectionIndex, template, position)}
              onDeleteSection={() => onDeleteSection(sectionIndex)}
              onDeleteControl={(controlIndex) => onDeleteControl(sectionIndex, controlIndex)}
              onMoveControl={(from, to) => onMoveControl(sectionIndex, from, to)}
              onMoveControlToSection={onMoveControlToSection}
              onDuplicateSection={() => onDuplicateSection(sectionIndex)}
              onRenameSection={(name) => onRenameSection(sectionIndex, name)}
            />
          ))
        )}
      </div>
    </div>
  );
}
